import { useState } from 'react';
import axios from 'axios';
import JSZip from 'jszip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { FilePdf, FileText, FileCsv, FileZip, Download, Images } from '@phosphor-icons/react';
import { useLanguage } from '@/contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ExportModal = ({ open, onClose, projectId, projectTitle, scenes = [] }) => {
  const { language } = useLanguage();
  const [exporting, setExporting] = useState(null);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const res = await axios.get(`${API}/projects/${projectId}/export/${format}`);
      const { filename, content, content_type, encoding } = res.data;

      // Create download
      let blob;
      if (encoding === 'base64') {
        const binaryString = atob(content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        blob = new Blob([bytes], { type: content_type });
      } else {
        blob = new Blob([content], { type: content_type });
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(language === 'es' ? `${format.toUpperCase()} exportado exitosamente!` : `${format.toUpperCase()} exported successfully!`);
    } catch (err) {
      toast.error(language === 'es' ? `Error al exportar ${format.toUpperCase()}` : `Failed to export ${format.toUpperCase()}`);
    } finally {
      setExporting(null);
    }
  };

  const handleDownloadImages = async () => {
    const scenesWithImages = scenes.filter(s => s.asset_url);
    if (scenesWithImages.length === 0) {
      toast.error(language === 'es' ? 'No hay imágenes para descargar. Genera imágenes primero.' : 'No images to download. Generate images first.');
      return;
    }

    setExporting('images');
    toast.info(language === 'es' ? `Preparando ZIP con ${scenesWithImages.length} imágenes...` : `Preparing ZIP with ${scenesWithImages.length} images...`);

    try {
      const zip = new JSZip();
      const imagesFolder = zip.folder('scenes');
      
      // Process each image and add to ZIP
      for (let i = 0; i < scenesWithImages.length; i++) {
        const scene = scenesWithImages[i];
        const imageUrl = scene.asset_url;
        const sceneNum = String(scene.scene_number).padStart(3, '0');
        
        // Update progress
        if (i % 10 === 0) {
          toast.info(`Processing image ${i + 1} of ${scenesWithImages.length}...`, { id: 'zip-progress' });
        }
        
        // Handle base64 data URLs
        if (imageUrl.startsWith('data:')) {
          const [header, base64Data] = imageUrl.split(',');
          const mimeType = header.match(/data:(.*?);/)[1];
          const extension = mimeType.includes('png') ? 'png' : 'jpg';
          
          // Add to ZIP
          imagesFolder.file(`scene_${sceneNum}.${extension}`, base64Data, { base64: true });
        } else {
          // External URL - try to fetch it
          try {
            const response = await fetch(imageUrl);
            if (response.ok) {
              const blob = await response.blob();
              const extension = imageUrl.includes('.png') ? 'png' : 'jpg';
              imagesFolder.file(`scene_${sceneNum}.${extension}`, blob);
            }
          } catch (fetchErr) {
            console.warn(`Could not fetch external image for scene ${sceneNum}:`, fetchErr);
          }
        }
      }
      
      // Generate and download ZIP
      toast.info('Creating ZIP file...', { id: 'zip-progress' });
      const zipBlob = await zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });
      
      // Download the ZIP
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${projectTitle?.replace(/[^a-z0-9]/gi, '_') || 'project'}_scenes.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(language === 'es' ? `¡ZIP descargado con ${scenesWithImages.length} imágenes!` : `Downloaded ZIP with ${scenesWithImages.length} images!`, { id: 'zip-progress' });
    } catch (err) {
      toast.error(language === 'es' ? 'Error al crear archivo ZIP' : 'Failed to create ZIP file');
      console.error(err);
    } finally {
      setExporting(null);
    }
  };

  const scenesWithImages = scenes.filter(s => s.asset_url).length;

  const exportOptions = [
    {
      id: 'images',
      name: language === 'es' ? 'Descargar Todas las Imágenes (ZIP)' : 'Download All Images (ZIP)',
      description: language === 'es' ? `${scenesWithImages} imágenes en un archivo ZIP` : `${scenesWithImages} images bundled in one ZIP file`,
      icon: FileZip,
      color: '#FF5D01',
      action: handleDownloadImages
    },
    {
      id: 'script',
      name: language === 'es' ? 'Guión Original' : 'Original Script',
      description: language === 'es' ? 'Descarga el guión subido como archivo de texto' : 'Download the uploaded script as text file',
      icon: FileText,
      color: '#8B5CF6'
    },
    {
      id: 'pdf',
      name: language === 'es' ? 'Storyboard en PDF' : 'PDF Storyboard',
      description: language === 'es' ? 'Documento visual de storyboard con resúmenes de escenas' : 'Visual storyboard document with scene summaries',
      icon: FilePdf,
      color: '#EF4444'
    },
    {
      id: 'csv',
      name: language === 'es' ? 'Hoja de Cálculo CSV' : 'CSV Spreadsheet',
      description: language === 'es' ? 'Todos los datos de escenas en formato de hoja de cálculo' : 'All scene data in spreadsheet format',
      icon: FileCsv,
      color: '#10B981'
    },
    {
      id: 'json',
      name: language === 'es' ? 'Manifiesto JSON' : 'JSON Manifest',
      description: language === 'es' ? 'Datos completos del proyecto con guía de edición' : 'Complete project data with editing guide',
      icon: FileText,
      color: '#2563EB'
    }
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#171717] border-[#333333] text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">{language === 'es' ? 'Exportar Proyecto' : 'Export Project'}</DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-3">
          {exportOptions.map((option) => {
            const Icon = option.icon;
            const isExporting = exporting === option.id;
            
            return (
              <button
                key={option.id}
                onClick={() => option.action ? option.action() : handleExport(option.id)}
                disabled={!!exporting}
                className="w-full p-4 bg-[#0a0a0a] border border-[#333333] rounded-lg hover:border-[#FF5D01] transition-colors flex items-center gap-4 text-left disabled:opacity-50"
                data-testid={`export-${option.id}-btn`}
              >
                <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${option.color}20` }}>
                  {isExporting ? (
                    <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: option.color }} />
                  ) : (
                    <Icon size={24} style={{ color: option.color }} />
                  )}
                </div>
                <div className="flex-1">
                  <div className="text-white font-medium">{option.name}</div>
                  <div className="text-sm text-[#A1A1AA]">{option.description}</div>
                </div>
                <Download size={20} className="text-[#A1A1AA]" />
              </button>
            );
          })}
        </div>
        <div className="text-xs text-[#A1A1AA] bg-[#0a0a0a] p-3 rounded-lg">
          <strong className="text-white">{language === 'es' ? 'Consejo:' : 'Tip:'}</strong> {language === 'es' ? 'Descargar Todas las Imágenes crea un archivo ZIP con escenas nombradas scene_001.png, scene_002.png, etc. para importar fácilmente en tu editor de video.' : 'Download All Images creates a single ZIP file with scenes named scene_001.png, scene_002.png, etc. for easy importing into your video editor.'}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-[#A1A1AA] hover:text-white">
            {language === 'es' ? 'Cerrar' : 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
